import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import ContactModal from "./ContactModal";
import { Wordmark } from "./Nav/Wordmark";
import { MegaMenu, MegaMenuRoot, type MegaMenuItem } from "./Nav/MegaMenu";
import { MobileNav } from "./Nav/MobileNav";

interface NavBarProps {
  ctaLabel?: string;
  forceTransparent?: boolean;
}

const NavBar = ({ ctaLabel = "Talk to Brick.", forceTransparent = false }: NavBarProps) => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const capabilities: MegaMenuItem[] = [
    {
      to: "/live",
      label: "Brick Live — AI training",
      description: "Practical online workshops in English",
    },
    {
      to: "/what-we-do",
      label: "All capabilities",
      description: "AI-native business design + build",
    },
    {
      to: "/ai-agent-development",
      label: "Agent and automation build",
      description: "Governed agents inside real workflows",
    },
    {
      to: "/ai-employees",
      label: "AI Employees",
      description: "A governed build capability—not the company category",
    },
  ];

  const company: MegaMenuItem[] = [
    { to: "/work", label: "Work", description: "Real evidence and working proof" },
    { to: "/about", label: "About", description: "Our point of view and philosophy" },
    { to: "/careers", label: "Careers" },
    { to: "#contact", label: "Contact" },
  ];


  const rawPath = location.pathname.replace(/\/$/, "") || "/";
  const currentPath = rawPath.replace(/^\/(sv|en)(?=\/|$)/, "") || "/";
  const isGroupActive = (items: MegaMenuItem[]) =>
    items.some(({ to }) => {
      if (!to || to.startsWith("#")) return false;
      return currentPath === to || currentPath.startsWith(to + "/");
    });

  const ctaButtonClass =
    "inline-flex items-center justify-center px-4 h-9 rounded-sm " +
    "text-[13px] font-medium tracking-[-0.01em] " +
    "bg-[hsl(var(--accent-brick))] text-[hsl(var(--fg-cream))] " +
    "hover:bg-[hsl(var(--accent-brick-hover))] transition-colors duration-150 ease-out " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--fg-cream))] " +
    "focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--bg-canvas))]";

  const handleMenuClickCapture = (event: React.MouseEvent<HTMLElement>) => {
    const anchor = (event.target as HTMLElement).closest('a[href$="#contact"]');
    if (!anchor) return;
    event.preventDefault();
    event.stopPropagation();
    setContactOpen(true);
  };

  const openChallenge = () => setContactOpen(true);

  return (
    <>
      <header
        className={`fixed top-[var(--promo-h,0px)] left-0 right-0 w-full z-[100] transition-all duration-200 ${
          !forceTransparent && scrolled
            ? "bg-[hsl(var(--bg-canvas))]/95 border-b border-[hsl(var(--border-hairline))]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Wordmark size={22} />

          <nav
            className="hidden md:flex items-center"
            aria-label="Primary"
            onClickCapture={handleMenuClickCapture}
          >
            <MegaMenuRoot>
              <Link
                to="/future-customer-experience"
                aria-current={currentPath === "/future-customer-experience" ? "page" : undefined}
                className={`relative inline-flex items-center h-9 px-1 mr-5 text-[13px] font-medium tracking-[-0.01em] transition-colors ${
                  currentPath === "/future-customer-experience"
                    ? "text-[hsl(var(--fg-cream))]"
                    : "text-[hsl(var(--fg-cream))]/80 hover:text-[hsl(var(--fg-cream))]"
                }`}
              >
                Future customer experience
                {currentPath === "/future-customer-experience" && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[hsl(var(--accent-brick))]"
                  />
                )}
              </Link>
              <MegaMenu
                label="What we do"
                items={capabilities}
                active={isGroupActive(capabilities)}
              />
              <Link
                to="/how-we-work"
                aria-current={currentPath === "/how-we-work" ? "page" : undefined}
                className={`relative inline-flex items-center h-9 px-1 text-[13px] font-medium tracking-[-0.01em] transition-colors ${
                  currentPath === "/how-we-work"
                    ? "text-[hsl(var(--fg-cream))]"
                    : "text-[hsl(var(--fg-cream))]/80 hover:text-[hsl(var(--fg-cream))]"
                }`}
              >
                How we work
                {currentPath === "/how-we-work" && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[hsl(var(--accent-brick))]"
                  />
                )}
              </Link>
              <MegaMenu label="Company" items={company} active={isGroupActive(company)} />
              
            </MegaMenuRoot>
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden md:block">
              <button type="button" onClick={openChallenge} className={ctaButtonClass}>
                {ctaLabel}
              </button>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-9 w-9 text-[hsl(var(--fg-cream))] hover:bg-white/[0.04]"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <MobileNav
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ctaLabel={ctaLabel}
        onCtaClick={openChallenge}
        onItemNavigate={(to) => {
          if (to === "#contact") {
            setContactOpen(true);
            return true;
          }
          return false;
        }}
        groups={[
          {
            label: "Future customer experience",
            items: [{ to: "/future-customer-experience", label: "The commercial spearhead" }],
          },
          { label: "What we do", items: capabilities },
          { label: "How we work", items: [{ to: "/how-we-work", label: "One challenge-led pathway" }] },
          { label: "Company", items: company },
          
        ]}
      />

      <ContactModal
        open={contactOpen}
        onOpenChange={setContactOpen}
        headless
        modalTitle="Start with the challenge."
        modalSubtitle="Tell us what is changing, what is not working or what you believe could be possible."
      />
    </>
  );
};

export default NavBar;
